"use client"

import { Navbar } from "@/components/navbar"
import { Footer } from "@/components/footer"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { PlayCircle, Users, Star, Clock, BookOpen, Sparkles, Rocket, Zap, ArrowLeft } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { useState, useEffect } from "react"

export default function Home() {
  const [isDark, setIsDark] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    const dark =
      localStorage.getItem("theme") === "dark" ||
      (!localStorage.getItem("theme") && window.matchMedia("(prefers-color-scheme: dark)").matches)
    setIsDark(dark)
  }, [])

  useEffect(() => {
    if (!mounted) return
    if (isDark) {
      document.documentElement.classList.add("dark")
      localStorage.setItem("theme", "dark")
    } else {
      document.documentElement.classList.remove("dark")
      localStorage.setItem("theme", "light")
    }
  }, [isDark, mounted])

  if (!mounted) return null

  return (
    <main className="min-h-screen text-foreground font-sans overflow-x-hidden">
      <Navbar isDark={isDark} setIsDark={setIsDark} />

      {/* Hero Section with enhanced animations and modern design */}
      <section className="relative pt-16 pb-24 lg:pt-32 lg:pb-48 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/15 blur-[100px] rounded-full animate-pulse-subtle" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-secondary/10 blur-[120px] rounded-full animate-pulse-subtle" />
        <div className="absolute top-1/2 left-1/4 w-80 h-80 bg-accent/10 blur-[100px] rounded-full animate-glow [animation-delay:2s]" />

        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-20">
            {/* Left Content */}
            <div className="flex-1 text-right space-y-8 order-2 lg:order-1 animate-slide-in-right">
              <div className="inline-flex items-center gap-3 px-6 py-2 rounded-full glass-morphism border border-white/40 dark:border-white/15 max-w-fit">
                <Sparkles className="h-5 w-5 text-secondary animate-pulse" />
                <span className="text-sm font-bold text-foreground/80">مستقبل التعليم بات حقيقة الآن</span>
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-tight tracking-tighter">
                تعلّم <span className="text-secondary italic">بذكاء</span> <br />
                <span className="relative inline-block">
                  أكثر من أي وقت
                  <svg
                    className="absolute -bottom-3 left-0 w-full h-3 text-secondary/30"
                    viewBox="0 0 300 20"
                    preserveAspectRatio="none"
                  >
                    <path d="M0 10 Q75 0 150 10 T300 10" fill="none" stroke="currentColor" strokeWidth="6" />
                  </svg>
                </span>
              </h1>

              <p className="text-lg lg:text-xl text-foreground/70 leading-relaxed max-w-2xl">
                منصة تعليمية عربية متطورة تجمع بين المحتوى الأكاديمي الرصين والتطبيق العملي المباشر مع أفضل الخبراء.
              </p>

              <div className="flex flex-wrap gap-4 justify-start flex-row-reverse pt-4">
                <Button
                  size="lg"
                  className="rounded-full px-10 text-lg h-14 shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 hover:scale-105 transition-all duration-300 group gap-2"
                >
                  ابدأ الآن
                  <Rocket className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-10 text-lg h-14 gap-2 hover:bg-primary/5 transition-colors bg-transparent"
                >
                  <PlayCircle className="h-5 w-5 text-primary" />
                  شاهد العرض التوضيحي
                </Button>
              </div>
            </div>

            {/* Right Hero Image */}
            <div className="flex-1 relative order-1 lg:order-2 animate-slide-in-left">
              <div className="absolute -top-12 -left-8 p-4 glass-morphism rounded-2xl shadow-xl z-20 animate-float">
                <div className="h-12 w-12 bg-primary/20 rounded-xl flex items-center justify-center text-primary">
                  <Zap className="h-6 w-6" />
                </div>
              </div>
              <div className="absolute top-1/3 -right-12 p-4 glass-morphism rounded-2xl shadow-xl z-20 animate-float [animation-delay:1s]">
                <div className="h-12 w-12 bg-secondary/20 rounded-xl flex items-center justify-center text-secondary">
                  <Users className="h-6 w-6" />
                </div>
              </div>
              <div className="absolute -bottom-8 left-8 p-4 glass-morphism rounded-2xl shadow-xl z-20 animate-float [animation-delay:2s]">
                <div className="h-12 w-12 bg-accent/20 rounded-xl flex items-center justify-center text-accent">
                  <Star className="h-6 w-6" />
                </div>
              </div>

              <div className="relative group max-w-[500px]">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/10 rounded-[3rem] blur-3xl group-hover:blur-2xl transition-all duration-700 opacity-60 group-hover:opacity-100" />
                <div className="relative overflow-hidden rounded-3xl shadow-2xl border-2 border-white/40 dark:border-white/10">
                  <Image
                    src="/images/original-946508194d926ae0121e516cb3d2a367.webp"
                    alt="Learning Platform Hero"
                    width={600}
                    height={700}
                    className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute -bottom-40 left-0 w-96 h-96 bg-secondary/5 blur-[120px] rounded-full" />

        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8 text-right">
            <div className="space-y-3 flex-1">
              <Badge className="bg-primary/15 text-primary border-0 font-black px-4 py-2 rounded-full w-fit text-xs uppercase tracking-wider">
                استكشف المجالات
              </Badge>
              <h2 className="text-4xl sm:text-5xl font-black leading-tight tracking-tighter">
                اختر مجال <span className="text-secondary">تخصصك</span>
              </h2>
              <p className="text-base text-foreground/65 font-medium leading-relaxed">
                مجالات متنوعة وشاملة لكل طموحاتك المهنية والأكاديمية
              </p>
            </div>
            <Link href="/categories">
              <Button
                variant="outline"
                className="rounded-full px-7 h-11 gap-2 hover:bg-primary/10 bg-transparent text-sm font-bold whitespace-nowrap"
              >
                اكتشف الكل
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {[
              { name: "البرمجة", icon: "💻", color: "from-blue-500 to-cyan-500", accent: "bg-blue-500/15" },
              { name: "التصميم", icon: "🎨", color: "from-purple-500 to-pink-500", accent: "bg-purple-500/15" },
              { name: "التسويق", icon: "📈", color: "from-orange-500 to-red-500", accent: "bg-orange-500/15" },
              { name: "البيانات", icon: "📊", color: "from-cyan-500 to-blue-500", accent: "bg-cyan-500/15" },
              { name: "الأعمال", icon: "💼", color: "from-orange-500 to-amber-500", accent: "bg-orange-500/15" },
              { name: "المهارات", icon: "⭐", color: "from-pink-500 to-rose-500", accent: "bg-pink-500/15" },
            ].map((cat, idx) => (
              <Link key={idx} href={`/categories?focus=${cat.name}`}>
                <Card className="group relative h-full border-0 shadow-md hover:shadow-xl transition-all duration-500 overflow-hidden rounded-2xl bg-white dark:bg-card/60 hover:translate-y-[-4px] cursor-pointer">
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${cat.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}
                  />

                  <div
                    className={`relative h-full p-5 lg:p-6 flex flex-col items-center justify-center text-center space-y-3 group-hover:space-y-4 transition-all duration-500`}
                  >
                    <div
                      className={`h-14 w-14 rounded-xl flex items-center justify-center text-2xl ${cat.accent} group-hover:scale-110 transition-all duration-500`}
                    >
                      {cat.icon}
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-foreground group-hover:text-primary transition-colors duration-300">
                      {cat.name}
                    </h3>
                    <div className="h-0.5 w-8 bg-gradient-to-r from-primary/0 via-primary to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-12">
            {[
              { label: "دورة معتمدة", value: "1,200+", icon: BookOpen, color: "text-blue-600 dark:text-blue-400" },
              { label: "مدرب خبير", value: "250+", icon: Users, color: "text-primary" },
              { label: "ساعة محتوى", value: "15,000+", icon: Clock, color: "text-orange-600 dark:text-orange-400" },
              { label: "تقييم", value: "4.9/5", icon: Star, color: "text-yellow-500" },
            ].map((stat, idx) => (
              <div
                key={idx}
                className="flex flex-col items-center text-center space-y-3 group p-6 rounded-2xl bg-white dark:bg-card hover:bg-muted/50 dark:hover:bg-card/80 transition-all duration-300 shadow-sm hover:shadow-lg"
              >
                <div
                  className={`h-16 w-16 rounded-2xl flex items-center justify-center ${stat.color} bg-opacity-10 group-hover:scale-110 transition-transform duration-500`}
                >
                  <stat.icon className="h-8 w-8" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-2xl sm:text-3xl font-black">{stat.value}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground font-bold">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Courses Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute -top-40 right-0 w-96 h-96 bg-primary/5 blur-[120px] rounded-full" />

        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6 text-right">
            <div className="space-y-4 max-w-3xl">
              <Badge className="bg-secondary/20 text-secondary border-none font-bold px-4 py-2 rounded-full w-fit">
                دورات مختارة
              </Badge>
              <h2 className="text-4xl sm:text-5xl font-black tracking-tight">ابدأ بأفضل الدورات</h2>
              <p className="text-lg text-foreground/70 font-medium">
                اختر من مكتبتنا الضخمة لأفضل الدورات التعليمية المتقدمة والمتخصصة.
              </p>
            </div>
            <Link href="/courses">
              <Button variant="outline" className="rounded-full px-8 h-12 gap-2 hover:bg-primary/10 bg-transparent">
                تصفح الكل
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "تطوير تطبيقات الويب الحديثة",
                instructor: "م. محمد كمال",
                price: "249",
                rating: "4.9",
                students: "3.2k",
                image: "web-development",
              },
              {
                title: "تصميم واجهات المستخدم UX/UI",
                instructor: "نورا علي",
                price: "189",
                rating: "4.8",
                students: "1.8k",
                image: "ui-design",
              },
              {
                title: "التسويق الرقمي الاحترافي",
                instructor: "سارة حسن",
                price: "159",
                rating: "4.7",
                students: "2.5k",
                image: "digital-marketing",
              },
            ].map((course, idx) => (
              <Link key={idx} href={`/courses/${idx + 1}`}>
                <Card className="group h-full border-0 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden rounded-3xl bg-white dark:bg-card hover:translate-y-[-8px]">
                  <div className="relative aspect-video overflow-hidden bg-muted">
                    <Image
                      src={`/.jpg?height=300&width=400&query=${course.image}`}
                      alt={course.title}
                      fill
                      className="object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-secondary text-white border-0 font-bold">محبوب</Badge>
                    </div>
                  </div>

                  <CardContent className="p-6 space-y-5 text-right">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-yellow-500">
                        <Star className="h-3 w-3 fill-current" />
                        <span className="text-xs font-black">{course.rating}</span>
                      </div>
                      <h3 className="text-lg font-black group-hover:text-primary transition-colors flex-1 ml-3">
                        {course.title}
                      </h3>
                    </div>

                    <div className="flex items-center justify-between py-3 border-t border-b border-muted/40">
                      <span className="text-xs text-muted-foreground">({course.students})</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">{course.instructor}</span>
                        <div className="h-7 w-7 rounded-full bg-gradient-to-br from-primary to-secondary overflow-hidden flex items-center justify-center text-white text-xs font-bold">
                          {course.instructor.charAt(0)}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2">
                      <Button className="rounded-full px-6 h-9 text-xs font-bold shadow-md hover:shadow-lg transition-all">
                        احجز الآن
                      </Button>
                      <span className="text-xl font-black text-primary">${course.price}</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 container mx-auto px-4">
        <div className="relative overflow-hidden rounded-[3.5rem] p-12 lg:p-24 text-center glass-morphism border-2 border-primary/20 group">
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary/20 rounded-full -mr-96 -mt-96 blur-3xl group-hover:scale-125 transition-transform duration-1000" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/10 rounded-full -ml-96 -mb-96 blur-3xl" />

          <div className="relative z-10 max-w-4xl mx-auto space-y-10">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">
              جاهز <span className="text-secondary">لتبدأ</span> رحلتك؟
            </h2>
            <p className="text-lg sm:text-xl text-foreground/70 leading-relaxed font-medium">
              انضم إلى آلاف الطلاب الذين غيروا حياتهم المهنية. ابدأ تعلمك الآن وحقق أحلامك.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                size="lg"
                className="rounded-full px-12 h-14 text-lg font-black shadow-xl shadow-primary/30 hover:shadow-2xl transition-all gap-2"
              >
                ابدأ مجاناً الآن
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-12 h-14 text-lg font-bold border-2 hover:bg-primary/10 transition-all bg-transparent"
              >
                تحدث معنا
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  )
}
